import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Plus, X, Calendar, Warning, Eye, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import StrategyMatrixSection from "../components/StrategyMatrixSection";
import StrategyWnbaTeams from "../components/StrategyWnbaTeams";

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

function TeamRow({ team, onRemove, disabled }) {
  return (
    <div className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2" data-testid={`team-row-${team.id}`}>
      <div>
        <div className="text-sm font-bold">{team.name}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{team.abbr} · ID {team.id}</div>
      </div>
      <button
        onClick={onRemove}
        disabled={disabled}
        data-testid={`remove-team-${team.id}`}
        className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[#FF3B30] disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function StrategyPanel({ label, accent, activeIds, allTeams, onAdd, onRemove, busy }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const activeTeams = allTeams.filter((t) => activeIds.includes(t.id));
  const remainingTeams = allTeams.filter((t) => !activeIds.includes(t.id));

  return (
    <div className={`bg-[#121212] border ${accent} rounded-sm p-5`} data-testid={`strategy-panel-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Analyst</div>
          <h3 className="font-display text-3xl">{label}</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Active teams</div>
          <div className="font-display text-3xl">{activeTeams.length}</div>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        {activeTeams.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">No teams active. Add at least one to start receiving picks.</div>
        ) : activeTeams.map((t) => (
          <TeamRow key={t.id} team={t} onRemove={() => onRemove(t.id)} disabled={busy} />
        ))}
      </div>

      {pickerOpen ? (
        <div className="space-y-2">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid={`add-team-select-${label.toLowerCase()}`}
            className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-sm font-mono-d"
          >
            <option value="">Choose a team…</option>
            {remainingTeams.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.abbr})</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => { setPickerOpen(false); setSelectedId(""); }}
              className="flex-1 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={async () => { if (!selectedId) return; await onAdd(Number(selectedId)); setPickerOpen(false); setSelectedId(""); }}
              disabled={!selectedId || busy}
              data-testid={`confirm-add-team-${label.toLowerCase()}`}
              className="flex-1 py-2 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-40 text-white text-xs uppercase tracking-wider font-bold rounded-sm"
            >
              Add team
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setPickerOpen(true)}
          disabled={busy}
          data-testid={`open-add-${label.toLowerCase()}`}
          className="w-full mt-3 flex items-center justify-center gap-2 py-2 border border-dashed border-white/15 hover:border-[#007AFF]/60 hover:text-[#007AFF] text-xs uppercase tracking-wider text-zinc-400 rounded-sm transition-colors"
        >
          <Plus size={14} /> Add team
        </button>
      )}
    </div>
  );
}

function HistoryRow({ change, teamMap, onRevert }) {
  const team = teamMap[change.team_id] || { name: `ID ${change.team_id}`, abbr: "?" };
  const inFuture = change.effective_date > today();
  const verb = change.action === "add" ? "Add" : "Remove";
  const verbColor = change.action === "add" ? "text-[#22C55E]" : "text-[#FF3B30]";
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2 text-xs" data-testid={`history-row-${change.change_id}`}>
      <div className="flex items-baseline gap-3">
        <span className={`text-[10px] uppercase tracking-widest font-bold ${verbColor}`}>{verb}</span>
        <span className="font-bold">{team.name}</span>
        <span className="text-zinc-500">→ {change.strategy === "iso" ? "Charlie" : "Dakota"}</span>
        <span className="text-zinc-600 font-mono-d">eff. {change.effective_date}</span>
      </div>
      {inFuture && (
        <button
          onClick={onRevert}
          data-testid={`revert-${change.change_id}`}
          className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[#FF3B30]"
        >
          Revert
        </button>
      )}
    </div>
  );
}

function TriggerMap({ map, teamMap }) {
  if (!map) return null;
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5" data-testid="trigger-map-section">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Eye size={18} className="text-[#007AFF]" />
          <h3 className="font-display text-2xl tracking-wider">Trigger Map · {map.date}</h3>
        </div>
        <div className="flex gap-3 text-[10px] uppercase tracking-widest">
          <span className="text-zinc-400">Total triggers <span className="font-bold text-white ml-1">{map.trigger_count}</span></span>
          <span className="text-[#22C55E]">Placed <span className="font-bold ml-1">{map.placed_count}</span></span>
          <span className="text-yellow-400">Skip-filter <span className="font-bold ml-1">{map.skipped_filter_count}</span></span>
          <span className="text-[#FF3B30]">Skip-both <span className="font-bold ml-1">{map.skipped_both_count}</span></span>
        </div>
      </div>
      {map.triggers.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-4 text-center">No qualifying triggers on this date. Either no team broke into L1 / W1-after-L, or this date hasn&apos;t been simulated yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-2 py-2">Team</th>
                <th className="text-left px-2 py-2">vs</th>
                <th className="text-left px-2 py-2">Trigger</th>
                <th className="text-left px-2 py-2">Outcome</th>
                <th className="text-left px-2 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {map.triggers.map((t, i) => {
                const team = teamMap[t.team_id] || { name: t.team_abbr };
                const outcomeStyle = t.result === "W" ? "bg-[#22C55E] text-black"
                  : t.result === "L" ? "bg-[#FF3B30] text-black"
                  : t.result === "SKIP-FILTER" ? "bg-yellow-400/20 text-yellow-400 border border-yellow-400/40"
                  : t.result === "SKIP-BOTH" ? "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/40"
                  : "bg-zinc-700 text-zinc-300";
                return (
                  <tr key={i} className="border-b border-white/5" data-testid="trigger-row">
                    <td className="px-2 py-1.5 font-bold">{team.name}</td>
                    <td className="px-2 py-1.5 text-xs text-zinc-400">{t.opponent}</td>
                    <td className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-300">{t.bet_type || (t.reason || "").includes("isolated") ? "iso-1L" : "WC"}</td>
                    <td className="px-2 py-1.5"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold ${outcomeStyle}`}>{t.result}</span></td>
                    <td className="px-2 py-1.5 text-xs text-zinc-500">{t.skipped_reason || t.reason || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function StrategyAdmin() {
  const [state, setState] = useState(null);
  const [map, setMap] = useState(null);
  const [mapDate, setMapDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [lockingNow, setLockingNow] = useState(false);
  const [pendingDate, setPendingDate] = useState(today());

  const teamMap = useMemo(() => {
    if (!state) return {};
    const m = {};
    state.all_teams.forEach((t) => { m[t.id] = t; });
    return m;
  }, [state]);

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/strategy/teams"),
        api.get(`/admin/strategy/trigger-map?date_str=${mapDate}`),
      ]);
      setState(a.data);
      setMap(b.data);
    } catch (e) { /* keep skeleton */ }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!state) return;
    api.get(`/admin/strategy/trigger-map?date_str=${mapDate}`).then((r) => setMap(r.data));
  }, [mapDate]); // eslint-disable-line

  const doChange = async (strategy, action, teamId) => {
    setBusy(true);
    try {
      const { data: resp } = await api.post("/admin/strategy/teams", {
        strategy, action, team_id: teamId, effective_date: pendingDate,
      });
      const teamName = teamMap[teamId]?.name || `Team ${teamId}`;
      const analyst = strategy === "iso" ? "Charlie" : "Dakota";
      const effLabel = pendingDate === today() ? "TODAY" : `eff. ${pendingDate}`;
      const newly = resp?.relocked?.newly_locked || 0;
      const lockedTail = action === "add" && resp?.relocked?.triggered
        ? (newly > 0 ? ` · ${newly} new pick${newly === 1 ? "" : "s"} locked for today` : " · today's slate unchanged (no new triggers)")
        : "";
      toast.success(`${action === "add" ? "Added" : "Removed"} ${teamName} → ${analyst} (${effLabel})${lockedTail}`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  const lockTodayMlb = async () => {
    setLockingNow(true);
    try {
      const { data } = await api.post("/admin/picks/lock-now", {});
      if ((data.newly_locked || 0) === 0 && (data.already_locked || 0) === 0) {
        toast.info(`No MLB picks qualified for today (${data.target_date})`);
      } else {
        toast.success(`MLB · locked ${data.newly_locked || 0} new pick${data.newly_locked === 1 ? "" : "s"} (${data.already_locked || 0} already locked)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lock failed");
    } finally { setLockingNow(false); }
  };

  const revert = async (changeId) => {
    setBusy(true);
    try {
      await api.delete(`/admin/strategy/teams/${changeId}`);
      toast.success("Queued change reverted");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return <div className="max-w-[1400px] mx-auto px-6 py-10 text-zinc-500 font-mono-d" data-testid="strategy-loading">Loading strategy state…</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Admin</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">STRATEGY CONTROL</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl">
          Add or remove teams from Charlie&apos;s iso-1L list or Dakota&apos;s WC list. Changes effective <span className="text-white font-bold">today</span> update the
          theoretical sim AND auto-relock today's locked slate. Future-dated changes apply to game dates ≥ effective date.
          <span className="text-[#22C55E]"> Historical settled picks are immutable</span> — actual performance % stays based on what was actually sent, regardless of later shortlist edits.
        </p>
      </div>

      <div className="bg-[#121212] border border-[#007AFF]/30 rounded-sm p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="effective-date-bar">
        <Calendar size={16} className="text-[#007AFF]" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Effective date for next change</div>
        <input
          type="date"
          value={pendingDate}
          min={today()}
          onChange={(e) => setPendingDate(e.target.value)}
          data-testid="effective-date-input"
          className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d"
        />
        <div className="text-[10px] text-zinc-500 italic flex items-center gap-1">
          <Warning size={12} /> changes effective today re-lock today's slate automatically
        </div>
        <button
          onClick={lockTodayMlb}
          disabled={lockingNow}
          data-testid="mlb-lock-now"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={12} className={lockingNow ? "animate-spin" : ""} /> Lock today's MLB picks now
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <StrategyPanel
          label="Charlie"
          accent="border-[#22C55E]/40"
          activeIds={state.active.iso}
          allTeams={state.all_teams}
          onAdd={(tid) => doChange("iso", "add", tid)}
          onRemove={(tid) => doChange("iso", "remove", tid)}
          busy={busy}
        />
        <StrategyPanel
          label="Dakota"
          accent="border-yellow-400/40"
          activeIds={state.active.wc}
          allTeams={state.all_teams}
          onAdd={(tid) => doChange("wc", "add", tid)}
          onRemove={(tid) => doChange("wc", "remove", tid)}
          busy={busy}
        />
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-8">
        <h3 className="font-display text-2xl tracking-wider mb-3">Change History</h3>
        {state.history.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">No changes yet — both strategies are at their original team sets.</div>
        ) : (
          <div>{state.history.map((c) => (<HistoryRow key={c.change_id} change={c} teamMap={teamMap} onRevert={() => revert(c.change_id)} />))}</div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="date"
          value={mapDate}
          onChange={(e) => setMapDate(e.target.value)}
          data-testid="trigger-map-date"
          className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d"
        />
        <button
          onClick={() => api.get(`/admin/strategy/trigger-map?date_str=${mapDate}`).then((r) => setMap(r.data))}
          data-testid="trigger-map-refresh"
          className="flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider rounded-sm"
        >
          <ArrowsClockwise size={14} /> Refresh
        </button>
      </div>
      <TriggerMap map={map} teamMap={teamMap} />
      <StrategyWnbaTeams />
      <StrategyMatrixSection mlbActiveTeams={{ iso: state.active.iso, wc: state.active.wc, allTeams: state.all_teams }} />
    </div>
  );
}
